import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { toastT } from "../lib/i18nToast";
import { KeyIcon } from "@heroicons/react/24/outline";
import { PageShell } from "../components/ui/PageShell";
import { Button, Card, EmptyState, Input, Select } from "../components/ui/primitives";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { apiClient } from "../lib/apiClient";
import { LoadingSpinner } from "../components/ui";
import { useLatest } from "../hooks/useLatest";

interface SecretDetail {
  id: string;
  name: string;
  description: string | null;
  projectId: string | null;
}

interface Project {
  id: string;
  name: string;
}

interface ProjectListResponse {
  items: Project[];
  totalCount: number;
  pageInfo?: {
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
  };
}

const api = (path: string, opts?: RequestInit) => apiClient(path, opts);

export const SecretEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { secretId = "" } = useParams<{ secretId: string }>();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const labelCls = `mb-1 block text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-700"}`;
  // Keeps loadSecret's mount effect from re-firing on a real language
  // switch; see useLatest's JSDoc.
  const tRef = useLatest(t);

  const [projects, setProjects] = useState<Project[]>([]);
  const [secret, setSecret] = useState<SecretDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");

  const loadProjects = useCallback(async () => {
    try {
      const res = await api("/api/projects?limit=100");
      if (!res.ok) return;
      const data = await res.json();
      const payload: ProjectListResponse = Array.isArray(data)
        ? { items: data, totalCount: data.length, pageInfo: { limit: 100, hasMore: false, nextCursor: null } }
        : data;
      setProjects(payload.items || []);
    } catch {
      // best effort
    }
  }, []);

  const loadSecret = useCallback(async () => {
    if (!secretId) return;
    setLoading(true);
    setError("");
    try {
      const res = await api(`/api/secrets/${encodeURIComponent(secretId)}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(String(data?.error || `Load failed (${res.status})`));
      }
      const current = data as SecretDetail;
      setSecret(current);
      setName(current.name || "");
      setDescription(current.description || "");
      setProjectId(current.projectId || "");
      setValue("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : tRef.current("secrets.error.load"),
      );
      setSecret(null);
    } finally {
      setLoading(false);
    }
  }, [secretId, tRef]);

  useEffect(() => {
    void loadProjects();
    void loadSecret();
  }, [loadProjects, loadSecret]);

  const save = async () => {
    if (!secret || !name.trim()) return;
    setSaving(true);
    setError("");
    try {
      const res = await api(`/api/secrets/${encodeURIComponent(secret.id)}`, {
        method: "PUT",
        body: JSON.stringify({
          name: name.trim(),
          value: value || undefined,
          description: description,
          projectId: projectId || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(String(data?.error || t("secrets.error.update")));
      }
      toastT.success("secrets.save");
      navigate(`/secrets/${secret.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("secrets.error.update");
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell
      maxWidth="3xl"
      title={t("secrets.edit.title")}
      subtitle={t("secrets.edit.subtitle")}
      actions={
        <>
          <Button size="sm" type="button" variant="secondary" onClick={() => navigate(secretId ? `/secrets/${secretId}` : "/secrets")} disabled={saving}>
            {t("secrets.cancel")}
          </Button>
          <Button size="sm" type="button" onClick={() => void save()} disabled={saving || !secret || !name.trim()}>
            {saving ? t("common.loading") : t("secrets.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-4 sm:space-y-5">
        {error && (
          <div className={`rounded-sm p-3 text-sm ${isDark ? "bg-red-900/50 text-red-200" : "bg-red-50 text-red-700"}`}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner />
          </div>
        ) : !secret ? (
          <EmptyState
            icon={<KeyIcon className="w-8 h-8" />}
            title={t("secrets.detail.notFound")}
            action={
              <Button type="button" size="sm" variant="secondary" onClick={() => navigate("/secrets")}>
                {t("secrets.detail.back")}
              </Button>
            }
          />
        ) : (
          <Card className="p-4 sm:p-5">
            <div className="grid gap-3">
              <div>
                <label className={labelCls}>{t("secrets.field.name")} <span className={isDark ? "text-red-400" : "text-red-600"}>*</span></label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                  className="font-mono"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className={labelCls}>{t("secrets.field.newValue")}</label>
                <Input
                  type="password"
                  placeholder={t("secrets.newValue.placeholder")}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>{t("secrets.field.description")}</label>
                <Input
                  type="text"
                  placeholder={t("secrets.description.placeholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>{t("secrets.field.project")}</label>
                <Select 
                  value={projectId} 
                  onChange={(value) => setProjectId(value)}
                  placeholder={t("secrets.noProject")}
                  options={projects.map((project) => ({
                    value: project.id,
                    label: project.name,
                  }))}
                />
              </div>
            </div>
          </Card>
        )}
      </div>
    </PageShell>
  );
};
